import type { TextValue, VariableMeta } from '@tsagko/label-contracts'
import { resolveContent } from '../variables'

/**
 * Shared low-level helpers for the build-commands pipeline.
 *
 * Hoisted into a single internal module in sub-task 2a.10.7 so they're
 * reachable from both `build-commands.ts` (the outer primitive dispatcher)
 * and `internal/compile-symbol.ts` (the symbol-specific sub-dispatcher).
 * Previously inlined in `build-commands.ts`; behaviour unchanged.
 */

/**
 * Convert a millimetre measurement to printer dots using
 * `dots = mm × dpi / 25.4`, rounded to the nearest integer. Centralised so
 * every primitive uses the same conversion (and rounding mode).
 */
export function mmToDots(mm: number, dpi: number): number {
  return Math.round((mm * dpi) / 25.4)
}

/**
 * Legacy `ValueSource` shape carried by templates persisted before the
 * 2026-05-24 `TextValue` migration. The current schema (`TextValueSchema`)
 * collapses both arms into a single `{ content: string }` shape where
 * `content` is either a pure literal or a `{variableName}` reference.
 *
 * The renderer keeps a backward-compat read for legacy fixtures that haven't
 * been migrated at the call site — see `resolveValueSource` below. Lint /
 * golden tests still seed the legacy shape directly.
 */
type LegacyStatic = { kind: 'static'; value: string }
type LegacyBound = { kind: 'bound'; fieldKey: string }
type LegacyValueSource = LegacyStatic | LegacyBound

/**
 * Resolve a text-value carrier (either the new `TextValue { content }` shape
 * or the legacy `{ kind, value | fieldKey }` shape) against a variables map
 * and a runtime data record.
 *
 * Resolution model (new shape):
 *   - `content === '<literal>'` → return literal verbatim.
 *   - `content === '{name}'`    → look up `data[name]`, fall back to
 *                                  `variables[name].default`, fall back to ''.
 *                                  Apply `variables[name].format` if set.
 *
 * Backward-compat (legacy shape):
 *   - `{ kind: 'static', value }` → return `value` verbatim.
 *   - `{ kind: 'bound', fieldKey }` → look up `data[fieldKey]`; missing /
 *                                      null / undefined → '' ; otherwise
 *                                      coerce with `String(...)`.
 *
 * The legacy arm exists so test fixtures and persisted templates that
 * predate Task 14 keep rendering byte-identically without forcing a
 * data-migration sweep through every consumer. New code should ALWAYS
 * construct `TextValue` and pass a variables map.
 */
export function resolveValueSource(
  source: TextValue | LegacyValueSource,
  data: Record<string, unknown>,
  variables: Record<string, VariableMeta> = {}
): string {
  if ('content' in source) {
    return resolveContent(source.content, variables, data)
  }
  if (source.kind === 'static') {
    return source.value
  }
  const raw = data[source.fieldKey]
  if (raw === undefined || raw === null) {
    return ''
  }
  return String(raw)
}

/**
 * Convert a font size in points to Zebra dots using `dots = pt × dpi / 72`,
 * rounded to the nearest integer. Centralised so every text-emitting branch
 * uses the same conversion (and rounding mode) at construction time — the
 * serializer no longer carries a hidden dpi assumption.
 */
export function sizePtToDots(sizePt: number, dpi: number): number {
  return Math.round((sizePt * dpi) / 72)
}
